import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../utils/urls.js';
import { useOnboarding } from '../Onboarding/OnboardingContext';

// 1. REACT ICON IMPORTS (Replacing SVGs)
import {
    FaSave,
    FaCheck,
    FaKey,
    FaLock,
    FaEye,
    FaEyeSlash,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaPlus,
    FaTrash,
    FaAddressCard,
    FaInfoCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import apiClient from '../utils/apiClient.js';

const initialOtherContactState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    can_login: false,
    address: '' // Full Address field
};

const ClientForm = ({ isNew = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { runTour, stepIndex, setStepIndex } = useOnboarding();

    // 2. PRIMARY FORM DATA STATE (Unchanged)
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        agency: '',
        is_agency: 0,
        email: '',
        password: '',
        phone_number: '',
        contact_person: '',
        billing_address: '',
        preferred_contact_method: 'Email',
        profile_image: '',
        state_or_province: '',
        county_or_region: '',
        is_active: true
    });

    // 3. NEW STATE FOR OTHER CONTACTS
    const [otherContacts, setOtherContacts] = useState([{ ...initialOtherContactState }]);

    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_new_password: ''
    });

    const [adminPasswordData, setAdminPasswordData] = useState({
        new_password: '',
        confirm_new_password: ''
    });

    const [message, setMessage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);

    const user = useMemo(() => {
        try {
            const userData = localStorage.getItem('user');
            return userData ? JSON.parse(userData) : null;
        } catch (e) {
            console.error('Error parsing user from localStorage:', e);
            return null;
        }
    }, []);

    // AUTO-FILL LOGIC FOR TOUR
    useEffect(() => {
        if (runTour && isNew && (stepIndex === 3 || stepIndex === 4)) {
            const randomSuffix = Math.floor(Math.random() * 10000);
            setFormData(prev => ({
                ...prev,
                first_name: 'John',
                last_name: 'Doe',
                agency: `ABC Legal Services ${randomSuffix}`,
                is_agency: 1,
                email: `john.doe.${randomSuffix}@example.com`,
                password: 'Password123!',
                phone_number: '555-0199',
                contact_person: 'John Doe',
                billing_address: '123 Main St, Springfield, IL 62704',
                preferred_contact_method: 'Email',
            }));
        }
    }, [runTour, isNew, stepIndex]);

    useEffect(() => {
        setTimeout(() => {
            setEmailCopied(false);
        }, 5000)
    }, [emailCopied])

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!isNew && id) {
                    const response = await axios.get(`${API_BASE_URL}/clients/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const data = response.data;

                    setFormData(prev => ({
                        ...prev,
                        ...data,
                        agency: data.agency || '',
                        website: data.website,
                        contact_person: data.contact_person || '',
                        billing_address: data.billing_address || '',
                        preferred_contact_method: data.preferred_contact_method || 'Email',
                        profile_image: data.profile_image || '',
                        state_or_province: data.state_or_province || null,
                        county_or_region: data.county_or_region || null,
                        is_active: data.is_active !== undefined ? data.is_active : true
                    }));

                    setOtherContacts(data.other_contacts && data.other_contacts.length > 0
                        ? data.other_contacts
                        : [{ ...initialOtherContactState }]
                    );
                }
            } catch (error) {
                setMessage('Error fetching client data.');
                console.error(error);
            }
        };

        if (!isNew && !user) {
            navigate('/login');
            return;
        }
        fetchUserData();
    }, [id, isNew, user, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : type === "number"
                        ? Number(value)
                        : value?.trimStart()  // prevents leading spaces
        }));
        setIsSaved(false);
    };


    const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    const handleAdminPasswordChange = (e) => setAdminPasswordData({ ...adminPasswordData, [e.target.name]: e.target.value });

    // 4. HANDLERS FOR OTHER CONTACTS
    const handleOtherContactChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        const updatedContacts = otherContacts.map((contact, i) =>
            i === index
                ? { ...contact, [name]: type === 'checkbox' ? checked : value }
                : contact
        );
        setOtherContacts(updatedContacts);
        setIsSaved(false);
    };

    const addOtherContact = () => {
        setOtherContacts([
            ...otherContacts,
            { ...initialOtherContactState },
        ]);
    };

    const removeOtherContact = (index) => {
        setOtherContacts(otherContacts.filter((_, i) => i !== index));
    };


    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     if (isNew && (!formData.email || !formData.password)) {
    //         setMessage('Email and Password are required for client creation.');
    //         return;
    //     }

    //     setIsSaving(true);
    //     setMessage(null);

    //     try {
    //         const token = localStorage.getItem('token');
    //         const dataToSubmit = {
    //             ...formData,
    //             other_contacts: otherContacts.filter(c => c.firstName || c.email)
    //         };

    //         if (!isNew) {
    //             if (!dataToSubmit.password) {
    //                 delete dataToSubmit.password;
    //             }
    //         }

    //         if (isNew) {
    //             const response = await axios.post(`${API_BASE_URL}/clients/create`, dataToSubmit, {
    //                 headers: { Authorization: `Bearer ${token}` }
    //             });
    //             toast.success('New client created successfully!');

    //             // Trigger Checklist Event
    //             await apiClient.post('/onboarding/events', { event_name: 'first_client_added' }).catch(() => {});

    //             if (runTour && stepIndex === 3) {
    //                 const newClientId = response.data.clientId || response.data.id;
    //                 setStepIndex(4); // Advance to Intake step
    //                 navigate(`/cases/new?clientId=${newClientId}`);
    //                 return;
    //             }

    //             setFormData({
    //                 first_name: '', last_name: '', agency: '', email: '', password: '',
    //                 phone_number: '', contact_person: '', billing_address: '',
    //                 preferred_contact_method: 'Email', profile_image: '', state_or_province: '',
    //                 county_or_region: '', is_active: true
    //             });
    //             setOtherContacts([{ ...initialOtherContactState }]);
    //             setIsSaved(true);
    //         } else {
    //             await axios.put(`${API_BASE_URL}/clients/${id}`, dataToSubmit, {
    //                 headers: { Authorization: `Bearer ${token}` }
    //             });
    //             toast.success('Client Updated successfully!');
    //             setIsSaved(true);
    //         }
    //     } catch (error) {
    //         setMessage(error.response?.data?.message || 'Failed to save client data.');
    //         console.error(error);
    //         toast.error('Failed to save client data.');
    //     } finally {
    //         setIsSaving(false);
    //     }
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isNew && (!formData.email || !formData.password)) {
            setMessage('Email and Password are required for client creation.');
            return;
        }

        setIsSaving(true);
        setMessage(null);

        try {
            const token = localStorage.getItem('token');
            const dataToSubmit = {
                ...formData,
                other_contacts: otherContacts.filter(c => c.firstName || c.email)
            };

            if (!isNew) {
                if (!dataToSubmit.password) {
                    delete dataToSubmit.password;
                }
            }

            if (isNew) {
                const response = await axios.post(`${API_BASE_URL}/clients/create`, dataToSubmit, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('New client created successfully!');

                // Trigger Checklist Event
                await apiClient.post('/onboarding/events', { event_name: 'first_client_added' }).catch(() => {});

                if (runTour && stepIndex === 3) {
                    const newClientId = response.data?.clientId || response.data?.id || response.data?.data?.id;
                    setStepIndex(4); // Advance to Intake step
                    // Prevent URL from saying "clientId=undefined"
                    if (newClientId) {
                        navigate(`/cases/new?clientId=${newClientId}`);
                    } else {
                        navigate(`/cases/new`); // The CaseForm fallback logic will take over
                    }
                    return;
                }

                setFormData({
                    first_name: '', last_name: '', agency: '', email: '', password: '',
                    phone_number: '', contact_person: '', billing_address: '',
                    preferred_contact_method: 'Email', profile_image: '', state_or_province: '',
                    county_or_region: '', is_active: true
                });
                setOtherContacts([{ ...initialOtherContactState }]);
                setIsSaved(true);
            } else {
                await axios.put(`${API_BASE_URL}/clients/${id}`, dataToSubmit, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Client Updated successfully!');
                setIsSaved(true);
            }
        } catch (error) {
            setMessage(error.response?.data?.message || 'Failed to save client data.');
            console.error(error);
            toast.error('Failed to save client data.');
        } finally {
            setIsSaving(false);
        }
    };



    const inputClass = "shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2";
    const labelClass = "block text-sm font-medium text-gray-700";

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
                {isNew ? 'Create New Client' : 'Edit Client Profile'}
            </h1>

            {message && (
                <div className={`p-3 rounded-md mb-4 ${message.includes('successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message}
                </div>
            )}

            <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Client Details</h2>
                <form onSubmit={handleSubmit}>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">

                        <div>
                            <label className={labelClass}>Agency</label>
                            <input type="text" name="agency" value={formData.agency} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Website</label>
                            <input type="text" name="website" value={formData.website} onChange={handleChange} className={inputClass} />
                        </div>

                        <div className="md:col-span-2">
                            <h3 className="text-lg font-semibold flex items-center text-[#2563EB] border-b pb-2 mt-4">
                                <FaAddressCard className="mr-2" /> Primary Contact Details
                            </h3>
                        </div>

                        <div>
                            <label className={labelClass}>First Name </label>
                            <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Last Name </label>
                            <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Email {isNew && <span className="text-red-500">*</span>}<span className="text-gray-400 font-normal"> | Client login Email</span></label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                ORG ID<span className="text-red-500">*</span>
                                <span className="text-red-400 font-normal">
                                    <b id="clientLoginEmail">
                                        {` (Required for client login)`}
                                    </b>
                                </span>
                                {formData.email && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${user.organizationId}`);
                                            setEmailCopied(true);
                                        }}
                                        className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
                                    >
                                        {emailCopied ? "Copied!" : "Copy"}
                                    </button>
                                )}
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={user?.organizationId || ''}
                                disabled
                                className={inputClass}
                            />
                        </div>

                        <div className="relative">
                            <label className={labelClass}>Password {isNew && <span className="text-red-500">*</span>}<span className="text-gray-400 font-normal"> | Client login Password</span></label>
                            <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} required={isNew} className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2 pr-10" />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[35px] text-gray-500 hover:text-gray-700 focus:outline-none">
                                {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                            </button>
                        </div>

                        <div>
                            <label className={labelClass}>Phone Number</label>
                            <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Contact Full Name</label>
                            <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange} className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Preferred Contact Method</label>
                            <select name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleChange} className={inputClass}>
                                <option value="Email">Email</option>
                                <option value="Phone">Phone</option>
                                <option value="Portal Notification">Portal Notification</option>
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelClass}>Billing Address</label>
                            <textarea name="billing_address" rows="3" value={formData.billing_address} onChange={handleChange} className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2 resize-y" />
                        </div>
                    </div>

                    <hr className="my-8 border-gray-200" />

                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold flex items-center text-[#2563EB]">
                                <FaUser className="mr-2" /> Other Contacts
                            </h3>
                            <button
                                type="button"
                                onClick={addOtherContact}
                                className="flex items-center bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition duration-150 text-sm"
                            >
                                <FaPlus className="mr-1" /> Add Contact
                            </button>
                        </div>

                        {otherContacts.map((contact, index) => (
                            <div key={index} className="border border-indigo-100 p-4 rounded-lg mb-4 bg-gray-50">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-md font-semibold text-gray-700">Additional Contact #{index + 1}</h4>
                                    {otherContacts.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeOtherContact(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>First Name</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={contact.firstName}
                                            onChange={(e) => handleOtherContactChange(index, e)}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Last Name</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={contact.lastName}
                                            onChange={(e) => handleOtherContactChange(index, e)}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={contact.email}
                                            onChange={(e) => handleOtherContactChange(index, e)}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Phone</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={contact.phone}
                                            onChange={(e) => handleOtherContactChange(index, e)}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelClass}>Full Address</label>
                                        <textarea
                                            name="address"
                                            rows="2"
                                            value={contact.address}
                                            onChange={(e) => handleOtherContactChange(index, e)}
                                            className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2 resize-y"
                                        />
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <input type="checkbox" name="can_login" checked={contact.can_login} onChange={(e) => handleOtherContactChange(index, e)} className="w-5 h-5 text-blue-600" />
                                        <label className={labelClass}>Can Login</label>
                                    </div>

                                    {contact.can_login && (
                                        <div>
                                            <label className={labelClass}>Password</label>
                                            <input
                                                type="text"
                                                name="password"
                                                value={contact.password}
                                                onChange={(e) => handleOtherContactChange(index, e)}
                                                className={inputClass}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end space-x-4 border-t pt-4">
                        <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <span className="tour-save-client-btn">
                            <button type="submit" disabled={isSaving || isSaved} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                {isSaved ? (<FaCheck className="w-5 h-5 mr-2" />) : (<FaSave className="w-5 h-5 mr-2" />)} {isSaving ? 'Saving...' : isSaved ? 'Saved' : 'Save Changes'}
                            </button>
                        </span>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClientForm;
